// services/featureFlagService.ts
//
// Feature flags now flow through the same runtime-config channel as every
// other piece of frontend config: `window._env_` (emitted by public/server.js
// from the App Service environment variables). The browser must never hold
// Azure resource credentials — the previous implementation instantiated
// `AppConfigurationClient` with `DefaultAzureCredential`, which the Azure SDK
// explicitly throws on in browser contexts:
//
//   "DefaultAzureCredential is not supported in the browser.
//    Use InteractiveBrowserCredential instead."
//
// That made the AppConfig client unusable, so `getAllFlags()` would
// effectively return `{}` even before this change. Removing the AppConfig
// dependency was therefore a no-op behaviorally.
//
// If we ever need true server-managed flags again, the canonical path is:
// add a per-flag entry to `App service > Configuration` (env-config.js will
// pick it up automatically), or — for dynamic flags — expose a small
// `/api/v1/config/feature-flags` endpoint on the BFF and fetch from there.

declare global {
  interface Window {
    _env_?: Record<string, string>;
  }
}

const FEATURE_FLAG_PREFIX = 'REACT_APP_FEATURE_';

function getEnv(key: string): string | undefined {
  return window._env_?.[key] ?? (typeof process !== 'undefined' ? process.env[key] : undefined);
}

function parseBool(value: string | undefined): boolean {
  if (!value) return false;
  const v = value.toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

function loadFlagsFromEnv(): Map<string, boolean> {
  const flags = new Map<string, boolean>();
  // Sources, in order of precedence:
  //   1. window._env_ (runtime-injected from App Service env)
  //   2. process.env (build-time fallback, mostly for local dev)
  const sources: Record<string, string | undefined>[] = [
    window._env_ ?? {},
    typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {},
  ];
  for (const source of sources) {
    for (const [k, v] of Object.entries(source)) {
      if (!k.startsWith(FEATURE_FLAG_PREFIX)) continue;
      const name = k.slice(FEATURE_FLAG_PREFIX.length).toLowerCase();
      if (!flags.has(name)) flags.set(name, parseBool(v));
    }
  }
  return flags;
}

class FeatureFlagService {
  private flags: Map<string, boolean> = new Map();
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    try {
      this.flags = loadFlagsFromEnv();
      this.isInitialized = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize feature flags:', error);
    }
  }

  getFeatureFlag(flagName: string): boolean {
    if (!this.isInitialized) {
      // eslint-disable-next-line no-console
      console.warn(`Feature flags not initialized yet. Flag "${flagName}" defaulting to false.`);
      return false;
    }
    return this.flags.get(flagName.toLowerCase()) || false;
  }

  getAllFlags(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    this.flags.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  // Kept for API compatibility with the previous AppConfig-backed impl.
  // Runtime config is static for the lifetime of the bundle (it would only
  // change on a redeploy), so refresh is a no-op rather than re-fetching.
  async refresh(): Promise<void> {
    this.flags = loadFlagsFromEnv();
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  destroy(): void {
    // No long-lived resources to clean up; method retained for API parity
    // with the previous implementation.
  }
}

const featureFlagService = new FeatureFlagService();
export default featureFlagService;
